import { create } from 'zustand';
import { api } from './api';
import { deviceName } from './device';
import { isIOS, isStandalone } from './install';

// Notifications on this device (Web Push). iPhone/iPad only allow them for the app added to the
// home screen (iOS 16.4+); everywhere else any modern browser works.

export type PushState = 'unsupported' | 'needs-install' | 'needs-app-update' | 'off' | 'on' | 'blocked';

// Inside the Android app the WebView has no web push: the app's own Firebase token is used instead.
const app = () => (typeof window !== 'undefined' ? window.VBNative : undefined);
const inApp = () => !!app();
const appPush = () => !!app()?.pushConfigured?.();
const APP_KEY = 'vb.pushApp'; // the token this device registered
const appRegistered = () => {
  try {
    return localStorage.getItem(APP_KEY) || '';
  } catch {
    return '';
  }
};
const setAppRegistered = (t: string) => {
  try {
    if (t) localStorage.setItem(APP_KEY, t);
    else localStorage.removeItem(APP_KEY);
  } catch {
    /* private mode */
  }
};
const until = async (ok: () => boolean, ms: number) => {
  for (const end = Date.now() + ms; Date.now() < end; await new Promise((r) => setTimeout(r, 300))) if (ok()) return true;
  return ok();
};

const supported = () => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const usePush = create<{ state: PushState; busy: boolean }>(() => ({ state: 'off', busy: false }));

export async function refreshPushState() {
  let state: PushState;
  if (inApp()) {
    state = !appPush() ? 'needs-app-update' : appRegistered() && app()!.notificationsAllowed?.() ? 'on' : 'off';
    usePush.setState({ state });
    return state;
  }
  if (isIOS && !isStandalone()) state = 'needs-install';
  else if (!supported()) state = 'unsupported';
  else if (Notification.permission === 'denied') state = 'blocked';
  else {
    const reg = await navigator.serviceWorker.getRegistration().catch(() => undefined);
    const sub = await reg?.pushManager.getSubscription().catch(() => null);
    state = sub && Notification.permission === 'granted' ? 'on' : 'off';
  }
  usePush.setState({ state });
  return state;
}

function b64ToBytes(b64url: string) {
  const pad = '='.repeat((4 - (b64url.length % 4)) % 4);
  const bin = atob((b64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

/** Must run from a tap (iOS asks for permission only then). */
export async function enablePush() {
  usePush.setState({ busy: true });
  try {
    if (inApp()) {
      const b = app()!;
      if (!b.notificationsAllowed?.()) {
        b.requestNotifications?.();
        if (!(await until(() => !!b.notificationsAllowed?.(), 20_000))) return refreshPushState();
      }
      await until(() => !!b.pushToken?.(), 10_000);
      const token = b.pushToken?.() ?? '';
      if (!token) throw new Error("Couldn't reach Google's notification service. Check the connection and try again.");
      await api.pushSubscribeApp(token, deviceName());
      setAppRegistered(token);
      return refreshPushState();
    }
    if ((await Notification.requestPermission()) !== 'granted') return refreshPushState();
    const reg = (await navigator.serviceWorker.getRegistration()) ?? (await navigator.serviceWorker.register('/sw.js'));
    await navigator.serviceWorker.ready;
    const { key } = await api.pushKey();
    const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(key) }));
    await api.pushSubscribe(sub.toJSON(), deviceName());
    return refreshPushState();
  } finally {
    usePush.setState({ busy: false });
  }
}

export async function disablePush() {
  usePush.setState({ busy: true });
  try {
    if (inApp()) {
      const t = appRegistered();
      if (t) await api.pushUnsubscribe('fcm:' + t).catch(() => undefined);
      setAppRegistered('');
      return refreshPushState();
    }
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      await api.pushUnsubscribe(sub.endpoint).catch(() => undefined);
      await sub.unsubscribe().catch(() => undefined);
    }
    return refreshPushState();
  } finally {
    usePush.setState({ busy: false });
  }
}

/** Keep the server's copy of this device's subscription current (it can change silently). */
export async function syncPushSubscription() {
  if ((await refreshPushState()) !== 'on') return;
  if (inApp()) {
    // Firebase may have rotated the token since: register the current one.
    const t = app()!.pushToken?.() ?? '';
    if (t && t !== appRegistered()) {
      await api.pushUnsubscribe('fcm:' + appRegistered()).catch(() => undefined);
      await api.pushSubscribeApp(t, deviceName()).then(() => setAppRegistered(t), () => undefined);
    }
    return;
  }
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) api.pushSubscribe(sub.toJSON(), deviceName()).catch(() => undefined);
}
